import type { MetricsSeriesPoint } from '@/features/metrics/metricsHelpers';

export type LineChartDatum = { [key: string]: number | string | null; label: string };
export type MiniMetricLineChartDatum = {
  label: string;
  measuredValue: number | null;
  carryForwardValue: number;
};

export const normalizeChartLabel = (label: string): string | null => {
  const trimmed = label.trim();
  if (trimmed.length >= 10 && trimmed.includes('-')) {
    return trimmed.slice(0, 10);
  }
  if (trimmed.length === 10 && trimmed.includes('/')) {
    const [day, month, year] = trimmed.split('/');
    if (day && month && year.length === 4) {
      return `${year}-${month}-${day}`;
    }
  }
  return null;
};

export const buildChartDates = (labels: string[], dates: string[]): string[] => {
  if (labels.length === 0 && dates.length === 0) return [];
  if (labels.length === 0) return dates;
  return labels.map((label, index) => {
    const normalized = normalizeChartLabel(label);
    if (normalized) return normalized;
    if (dates.length > index) return dates[index];
    return label;
  });
};

const buildValuesByLabel = (
  series: MetricsSeriesPoint[] | undefined,
): Map<string, number | null> => {
  const valuesByLabel = new Map<string, number | null>();
  series?.forEach((point) => {
    valuesByLabel.set(point.label, point.value);
    const normalized = normalizeChartLabel(point.label);
    if (normalized) {
      valuesByLabel.set(normalized, point.value);
    }
  });
  return valuesByLabel;
};

const resolveLineChartValue = (
  valuesByLabel: Map<string, number | null>,
  series: MetricsSeriesPoint[] | undefined,
  labels: string[],
  dates: string[],
  index: number,
): number | null => {
  const label = labels[index];
  if (!label) return null;

  const valueByLabel = valuesByLabel.get(label);
  if (typeof valueByLabel === 'number') {
    return valueByLabel;
  }

  const normalizedLabel = normalizeChartLabel(label);
  if (normalizedLabel) {
    const valueByNormalizedLabel = valuesByLabel.get(normalizedLabel);
    if (typeof valueByNormalizedLabel === 'number') {
      return valueByNormalizedLabel;
    }
  }

  const dateKey = dates[index];
  if (dateKey) {
    const normalizedDateKey = normalizeChartLabel(dateKey) ?? dateKey.slice(0, 10);
    const valueByDate = valuesByLabel.get(dateKey);
    if (typeof valueByDate === 'number') {
      return valueByDate;
    }
    const valueByNormalizedDate = valuesByLabel.get(normalizedDateKey);
    if (typeof valueByNormalizedDate === 'number') {
      return valueByNormalizedDate;
    }
  }

  if (series && series.length === labels.length) {
    const seriesValue = series[index]?.value;
    if (typeof seriesValue === 'number') {
      return seriesValue;
    }
  }

  return null;
};

export const buildLineChartSeries = (
  series: MetricsSeriesPoint[] | undefined,
  labels: string[],
  dates: string[],
  key: string,
): LineChartDatum[] | null => {
  if (labels.length === 0) return null;
  const valuesByLabel = buildValuesByLabel(series);
  return labels.map((label, index) => {
    const resolvedValue = resolveLineChartValue(valuesByLabel, series, labels, dates, index);
    return { label, [key]: resolvedValue };
  });
};

export const buildMiniMetricLineChartSeries = (
  series: MetricsSeriesPoint[] | undefined,
  labels: string[],
  dates: string[],
  measuredLabels?: Set<string>,
): MiniMetricLineChartDatum[] | null => {
  if (labels.length === 0) return null;
  const valuesByLabel = buildValuesByLabel(series);
  let carryForwardValue = 0;

  return labels.map((label, index) => {
    const resolvedValue = resolveLineChartValue(valuesByLabel, series, labels, dates, index);
    const measuredValue =
      measuredLabels !== undefined && !measuredLabels.has(label) ? null : resolvedValue;
    if (measuredValue !== null) {
      carryForwardValue = measuredValue;
    }

    return {
      label,
      measuredValue,
      carryForwardValue,
    };
  });
};
